/**
 * Leaflet host. Lazy-loads Leaflet, builds the map once, and re-renders
 * markers whenever the dataset changes.
 *
 * Keeps a single Leaflet instance per `<MapCanvas>` mount — Strict-mode's
 * double-render is handled via an idempotent init guarded by a ref.
 */
import { useEffect, useRef, useState } from "react";
import {
  loadLeaflet,
  loadMarkerCluster,
  type LeafletCluster,
  type LeafletClusterGroup,
  type LeafletLayer,
  type LeafletMap,
  type LeafletMarker,
  type LeafletNS,
} from "../../../shared/leaflet-loader";
import type { MapBounds, MapCasePoint } from "../../../shared/api";
import {
  getTileUrlTemplate,
  TILE_ATTRIBUTION,
} from "../../../shared/map-tiles";

// Minimal subset of Leaflet's runtime map API we touch here that isn't on
// the typed `LeafletMap` interface. Keeping these inline avoids pulling in
// `@types/leaflet` for one method.
interface LeafletMapWithBounds extends LeafletMap {
  on(event: string, handler: () => void): void;
  off(event: string, handler: () => void): void;
  getZoom(): number;
  getBounds(): {
    getWest(): number;
    getSouth(): number;
    getEast(): number;
    getNorth(): number;
  };
}

// circleMarker's restyle methods, absent from the minimal `LeafletMarker`.
interface StylableMarker extends LeafletMarker {
  setRadius(radius: number): void;
  setStyle(style: Record<string, unknown>): void;
}

// We stash each building's case count on its marker so the cluster's
// iconCreateFunction can sum *cases* (not markers) for the bubble label.
interface CaseMarker extends LeafletMarker {
  connyCases?: number;
}
import { useLocale, t } from "../../../shared/i18n";
import {
  CLOSED_COLOR,
  OPEN_COLOR,
  type MapColorMode,
  type MapDisplayMode,
} from "./constants";
import {
  clusterBadge,
  CLUSTER_DISABLE_ZOOM,
  dotStyleForZoom,
  topLandlord,
} from "./stats";

export interface Props {
  points: MapCasePoint[];
  defaultCenter: [number, number];
  defaultZoom: number;
  bbox: MapBounds | null;
  /**
   * Optional point to zoom to once the map is ready (e.g. user's ZIP
   * centroid or a search pick). `seq` distinguishes repeated picks of the
   * same place — the fly-to effect keys on these values, so bump it to
   * re-trigger the jump even when lat/lng/zoom are unchanged.
   */
  focus?: { lat: number; lng: number; zoom?: number; seq?: number } | null;
  /**
   * Fires when the user finishes panning/zooming. The parent uses this to
   * recompute the active scope (and therefore the visible points + stats).
   */
  onBoundsChange?: (bounds: MapBounds) => void;
  /**
   * How points are laid out: "cluster" (default), "dots" or "heat". Flipped
   * live from the toolbar selector; reconciled in place so the view survives.
   */
  mode?: MapDisplayMode;
  /** Dot colouring — see `MapColorMode`. Defaults to "status". */
  colorMode?: MapColorMode;
}

function fmtEuroDe(n: number): string {
  return new Intl.NumberFormat("de-DE", { maximumFractionDigits: 0 }).format(
    Math.round(n),
  );
}

function buildPopupHtml(p: MapCasePoint, locale: "en" | "de"): string {
  const top = topLandlord(p.landlords);
  const noun =
    p.cases === 1
      ? t("map.popupCase", {}, locale)
      : t("map.popupCasesPlural", {}, locale);
  const parts: string[] = [
    `<div class="cn-map-popup__title">${escapeHtml(p.street)} ${escapeHtml(p.house_number)}</div>`,
    `<div class="cn-map-popup__zip">${escapeHtml(p.zip_code)}</div>`,
  ];
  if (p.cws > 0) {
    parts.push(
      `<div class="cn-map-popup__eur">${t(
        "map.popupAvgPerMonth",
        { eur: fmtEuroDe(p.total / p.cws) },
        locale,
      )}</div>`,
    );
  }
  parts.push(
    `<div class="cn-map-popup__meta">${t(
      "map.popupCases",
      { n: p.cases, noun, closed: p.closed, open: p.open },
      locale,
    )}</div>`,
  );
  if (top) {
    parts.push(
      `<div class="cn-map-popup__meta">${t(
        "map.popupLandlord",
        { name: escapeHtml(top[0]) },
        locale,
      )}</div>`,
    );
  }
  if (p.years.length) {
    parts.push(
      `<div class="cn-map-popup__meta">${t(
        "map.popupLastClosed",
        { year: Math.max(...p.years) },
        locale,
      )}</div>`,
    );
  }
  return parts.join("");
}

function escapeHtml(s: string): string {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export function MapCanvas({
  points,
  defaultCenter,
  defaultZoom,
  bbox,
  focus,
  onBoundsChange,
  mode = "cluster",
  colorMode = "status",
}: Props) {
  const hostRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<LeafletMap | null>(null);
  const markersRef = useRef<LeafletMarker[]>([]);
  const clusterRef = useRef<LeafletClusterGroup | null>(null);
  // Bumped on every layer rebuild so a stale async cluster-plugin load
  // resolving after the mode or dataset changed again is ignored.
  const layerGenRef = useRef(0);
  const lRef = useRef<LeafletNS | null>(null);
  // Kept across renders so the cleanup in the init effect can tear them
  // down. See the `invalidateSize` block inside the init effect for the
  // "why we need both" backstory.
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const pageShowHandlerRef = useRef<(() => void) | null>(null);
  // `mapReady` is bumped each time Leaflet finishes loading and the map
  // instance is live. We use it as a dependency on the markers + focus
  // effects so they re-run as soon as the map is ready, even if `points`
  // / `bbox` arrived before Leaflet's async loader resolved. Without this
  // the very first render shows an empty map until the user pans or zooms.
  const [mapReady, setMapReady] = useState(0);
  const locale = useLocale();
  // We stash the latest callback in a ref so the one-time map-init effect
  // can read it without re-subscribing whenever the parent re-renders.
  const onBoundsChangeRef = useRef(onBoundsChange);
  onBoundsChangeRef.current = onBoundsChange;
  // Same trick for locale — the cluster iconCreateFunction closure is built
  // once at map init but must format counts in the *current* locale.
  const localeRef = useRef(locale);
  localeRef.current = locale;
  const roRef = useRef<ResizeObserver | null>(null);

  // One-time: load Leaflet (+ the markercluster plugin when clustering) and
  // create the map. If the plugin CDN fails we degrade to plain dots rather
  // than showing no map at all.
  useEffect(() => {
    let cancelled = false;
    // Always create the map on bare Leaflet; the cluster group (if any) is
    // attached by the [cluster] effect below, so clustering can be flipped at
    // runtime without rebuilding the map.
    loadLeaflet().then((L) => {
      if (cancelled || !hostRef.current || mapRef.current) return;
      lRef.current = L;
      // `preferCanvas` draws the circle markers on one <canvas> instead of
      // thousands of SVG nodes — with the full ~10k-point dataset SVG makes
      // pan/zoom visibly sluggish. Popups/click hits work the same.
      const map = L.map(hostRef.current, {
        zoomControl: false,
        preferCanvas: true,
      }).setView(defaultCenter, defaultZoom) as LeafletMapWithBounds;
      L.control.zoom({ position: "bottomright" }).addTo(map);
      L.tileLayer(getTileUrlTemplate(), {
        attribution: TILE_ATTRIBUTION,
        subdomains: "abcd",
        maxZoom: 19,
      }).addTo(map);
      mapRef.current = map;

      // Heyflow (and other iframe hosts) expand their container *after* the
      // map initialises, so Leaflet calculates tiles for the wrong size.
      // A ResizeObserver catches every dimension change and asks Leaflet to
      // recalculate, which loads the missing tiles immediately.
      const ro = new ResizeObserver(() => map.invalidateSize());
      ro.observe(hostRef.current);
      roRef.current = ro;

      // Wake up the markers effect now that the map exists. We deliberately
      // do NOT use this as a dep on the focus effect — re-running focus on
      // every map (re)mount would snap the user's view back to the initial
      // bbox/focus mid-pan. Instead we apply the initial focus/bbox here,
      // once, while the map is being created.
      setMapReady((n) => n + 1);

      // Leaflet caches the container's pixel size at init time. Inside
      // Heyflow the screen transition often hasn't settled yet — the
      // container measures 0×0 (or the wrong width), tiles render for
      // that viewport, and the user sees a gray pane until they pan.
      // We force a recalculation here on `requestAnimationFrame` (catches
      // "container resized between init and first paint") and again
      // 200ms later (catches Heyflow's CSS screen-transition).
      const kick = () => {
        const m = mapRef.current as LeafletMapWithBounds | null;
        if (!m) return;
        (
          m as unknown as { invalidateSize: (animate?: boolean) => void }
        ).invalidateSize(false);
      };
      requestAnimationFrame(kick);
      setTimeout(kick, 200);

      // Long-lived: any future container resize (Heyflow screen change,
      // mobile orientation flip, keyboard open/close) should re-measure
      // immediately. Stash the observer so we can disconnect on unmount.
      if (typeof ResizeObserver !== "undefined" && hostRef.current) {
        const ro = new ResizeObserver(() => kick());
        ro.observe(hostRef.current);
        resizeObserverRef.current = ro;
      }
      // bfcache restore (back/forward navigation) — same problem.
      window.addEventListener("pageshow", kick);
      pageShowHandlerRef.current = kick;
      if (focus) {
        map.setView([focus.lat, focus.lng], focus.zoom ?? 14, {
          animate: false,
        });
      } else if (bbox) {
        map.fitBounds(
          [
            [bbox.south, bbox.west],
            [bbox.north, bbox.east],
          ],
          { padding: [24, 24] },
        );
      }

      // Push the initial viewport up so the parent can compute stats for
      // what's actually visible, then on every pan/zoom.
      const emit = () => {
        const cb = onBoundsChangeRef.current;
        if (!cb) return;
        const b = map.getBounds();
        cb({
          west: b.getWest(),
          south: b.getSouth(),
          east: b.getEast(),
          north: b.getNorth(),
        });
      };
      map.on("moveend", emit);
      // Initial emit — wait one tick so the size is settled.
      setTimeout(emit, 0);

      // Dots shrink/fade at city zoom and grow solid at street zoom.
      // Restyle in place on zoom changes — far cheaper than recreating
      // thousands of markers.
      const restyle = () => {
        const s = dotStyleForZoom(map.getZoom());
        for (const m of markersRef.current) {
          const sm = m as StylableMarker;
          sm.setRadius(s.radius);
          sm.setStyle({ fillOpacity: s.fillOpacity });
        }
      };
      map.on("zoomend", restyle);
    });
    return () => {
      cancelled = true;
      roRef.current?.disconnect();
      roRef.current = null;
      const m = mapRef.current;
      mapRef.current = null;
      markersRef.current = [];
      clusterRef.current = null;
      lRef.current = null;
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
      if (pageShowHandlerRef.current) {
        window.removeEventListener("pageshow", pageShowHandlerRef.current);
        pageShowHandlerRef.current = null;
      }
      if (m) m.remove();
    };
    // defaultCenter/defaultZoom are only used for the *initial* view; later
    // changes route through `focus` (see effect below). Clustering is owned by
    // the [cluster] effect below. Deliberately empty deps to keep the map
    // instance stable across re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Build the active layer for the current display mode — in place, so the
  // user's pan/zoom survives (rebuilding the map would snap back to the opening
  // frame). Re-runs on dataset / mode / colour / locale changes. A generation
  // token cancels a stale plugin load if the mode or dataset changes again
  // before it resolves. The markercluster plugin loads on demand; if its CDN
  // fails we fall back to plain dots so the map is never empty.
  useEffect(() => {
    const map = mapRef.current as LeafletMapWithBounds | null;
    const L = lRef.current;
    if (!map || !L || !mapReady) return;
    const gen = ++layerGenRef.current;

    // ── Tear down whatever is currently shown ────────────────────────────
    if (clusterRef.current) {
      clusterRef.current.clearLayers();
      map.removeLayer(clusterRef.current as unknown as LeafletLayer);
      clusterRef.current = null;
    }
    for (const m of markersRef.current) map.removeLayer(m);
    markersRef.current = [];

    // Build the circle markers for the current dataset. Not yet added.
    const makeMarkers = (): LeafletMarker[] => {
      const style = dotStyleForZoom(map.getZoom());
      const out: LeafletMarker[] = [];
      for (const p of points) {
        const color =
          colorMode === "won" || p.closed >= p.open ? CLOSED_COLOR : OPEN_COLOR;
        const marker = L.circleMarker([p.lat, p.lng], {
          radius: style.radius,
          color,
          fillColor: color,
          fillOpacity: style.fillOpacity,
          weight: 1,
        });
        (marker as CaseMarker).connyCases = p.cases;
        marker.bindPopup(buildPopupHtml(p, locale));
        out.push(marker);
      }
      return out;
    };

    // ── Dots ─────────────────────────────────────────────────────────────
    if (mode === "dots") {
      const markers = makeMarkers();
      markersRef.current = markers;
      for (const m of markers) m.addTo(map);
      return;
    }

    // ── Cluster (default) ────────────────────────────────────────────────
    // Hold the dots off the bare map until the group is ready (avoids adding
    // ~10k markers twice); fall back to flat dots if the plugin fails.
    const markers = makeMarkers();
    markersRef.current = markers;
    loadMarkerCluster()
      .then((LC) => {
        if (gen !== layerGenRef.current || !mapRef.current) return;
        if (!LC.markerClusterGroup) {
          for (const m of markers) m.addTo(map);
          return;
        }
        const group = LC.markerClusterGroup({
          chunkedLoading: true, // keep the UI responsive while 10k+ markers index
          showCoverageOnHover: false,
          spiderfyOnMaxZoom: false,
          maxClusterRadius: 60,
          disableClusteringAtZoom: CLUSTER_DISABLE_ZOOM,
          iconCreateFunction: (c: LeafletCluster) => {
            let total = 0;
            for (const m of c.getAllChildMarkers()) {
              total += (m as CaseMarker).connyCases ?? 0;
            }
            const b = clusterBadge(total, localeRef.current);
            return L.divIcon({
              className: `cn-map-cluster cn-map-cluster--${b.tier}`,
              html: `<span>${b.label}</span>`,
              iconSize: [b.px, b.px],
            });
          },
        });
        group.addTo(map);
        clusterRef.current = group;
        group.addLayers(markers);
      })
      .catch(() => {
        // eslint-disable-next-line no-console
        console.warn(
          "[conny] markercluster failed to load — showing plain dots",
        );
        if (
          gen === layerGenRef.current &&
          mapRef.current &&
          !clusterRef.current
        )
          for (const m of markers) m.addTo(map);
      });
  }, [points, mode, mapReady, colorMode, locale]);

  // React to externally-provided focus (e.g. user's ZIP centroid or a search
  // pick) AFTER the initial mount. We deliberately do NOT depend on `mapReady`
  // or `bbox` here — re-applying the bbox every time it arrives would snap the
  // user back to the city overview while they're panning. The initial
  // bbox/focus is handled inside the map-init callback above; this effect only
  // flies to a focus when its actual coordinates change.
  //
  // We key the effect on the primitive lat/lng/zoom — NOT the `focus` object —
  // because the parent rebuilds that object on every render. Depending on the
  // object identity made this re-run each render while a search pick was
  // active, so `setView` fired in a loop: every pan/zoom (or marker-popup
  // auto-pan) triggered a re-render, which snapped the view straight back and
  // made the map feel "locked". Keying on the values fires the fly-to once per
  // pick, then leaves the user free to roam.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !focus) return;
    map.setView([focus.lat, focus.lng], focus.zoom ?? 14, { animate: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus?.lat, focus?.lng, focus?.zoom, focus?.seq]);

  return <div ref={hostRef} className="cn-map-canvas" />;
}
